import React, { useState } from 'react';
import { signIn, signInWithGoogle } from './firebase';
import './auth.css';

export default function Login({ bootstrapError }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signIn(email.trim(), password);
    } catch (err) {
      const code = err?.code || '';
      if (code.includes('user-not-found') || code.includes('wrong-password') || code.includes('invalid-credential')) {
        setError('Invalid email or password.');
      } else if (code.includes('too-many-requests')) {
        setError('Too many attempts. Try again shortly.');
      } else if (code.includes('user-disabled')) {
        setError('Account disabled. Contact admin.');
      } else {
        setError(err?.message || 'Sign-in failed.');
      }
    } finally {
      setLoading(false);
    }
  };

  const onGoogle = async () => {
    setError('');
    setGoogleLoading(true);
    try {
      await signInWithGoogle();
    } catch (err) {
      const code = err?.code || '';
      if (code.includes('popup-closed')) {
        // user cancelled, no error
      } else if (code.includes('popup-blocked')) {
        setError('Pop-up was blocked. Allow pop-ups and retry.');
      } else if (code.includes('account-exists-with-different-credential')) {
        setError('This email is registered with a different sign-in method.');
      } else {
        setError(err?.message || 'Google sign-in failed.');
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="signal-auth-root" data-testid="login-screen">
      <div className="signal-auth-bg" aria-hidden="true" />
      <div className="signal-auth-card">
        <div className="signal-auth-brand">
          <div className="signal-auth-mark" />
          <div className="signal-auth-brand-text">
            <div className="signal-auth-logo">SIGNAL</div>
            <div className="signal-auth-tagline">INTERNAL CONTROL PLANE</div>
          </div>
        </div>

        <div className="signal-auth-title">Restricted Access</div>
        <div className="signal-auth-sub">
          This console is limited to <strong>Admin</strong> and <strong>Tech Support</strong> roles. All activity is logged.
        </div>

        <form className="signal-auth-form" onSubmit={onSubmit} autoComplete="on">
          <label className="signal-auth-label">
            <span>EMAIL</span>
            <input
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@signalai.buzz"
              data-testid="login-email-input"
            />
          </label>
          <label className="signal-auth-label">
            <span>PASSWORD</span>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••••••"
              data-testid="login-password-input"
            />
          </label>

          {(bootstrapError || error) && (
            <div className="signal-auth-error" data-testid="login-error">
              {bootstrapError
                ? 'Authentication setup error. Verify Firebase environment variables and reload.'
                : error}
            </div>
          )}

          <button type="submit" className="signal-auth-submit" disabled={loading} data-testid="login-submit-btn">
            {loading ? 'AUTHENTICATING…' : 'ENTER CONTROL PLANE'}
          </button>
        </form>

        <div className="signal-auth-divider">
          <span>OR</span>
        </div>

        <button
          type="button"
          className="signal-auth-google"
          onClick={onGoogle}
          disabled={googleLoading}
          data-testid="login-google-btn"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83c.87-2.6 3.3-4.52 6.16-4.52z" />
          </svg>
          <span>{googleLoading ? 'OPENING GOOGLE…' : 'CONTINUE WITH GOOGLE'}</span>
        </button>

        <div className="signal-auth-footnote">
          Default deny. No public access. Unauthorized entry attempts are recorded.
        </div>
      </div>
    </div>
  );
}
